// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * This module provides functionality to update existing locations in the system.
 * Supports partial updates of location attributes.
 */
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { LocationType, locationSchema } from '@/location/api/types';
import { LocationComponents } from '@/sparse/api/types';
import { z } from 'zod';

/**
 * Schema for validating the API response when updating a location.
 * Transforms the response to provide a cleaner interface with the updated location data.
 */
const updateLocationResponseSchema = z
  .object({
    data: locationSchema, // The updated location data
    metadata: responseMetadataSchema, // Request tracking metadata
  })
  .transform(({ data, ...response }) => ({ ...response, location: data }));

type UpdateLocationResponse = z.infer<typeof updateLocationResponseSchema>;

/**
 * Interface defining the parameters that can be updated for a location.
 * All fields are optional, allowing for partial updates.
 */
export interface UpdateLocationParameters {
  name?: string; // New display name
  address?: LocationComponents | null;
  isArchived?: boolean; // New archive status
  isInventory?: boolean; // New inventory location status
  isTransient?: boolean; // New transient location status
  customerId?: string | null; // New customer ownership
  description?: string | null; // New description (zones)
  consignee?: string | null; // New consignee company ID (zones)
  isInventoryOverride?: boolean; // Whether zone overrides parent inventory (zones)
}

/**
 * Updates an existing location in the system.
 * Supports partial updates - only provided parameters will be modified.
 *
 * @param key - The unique key identifier for the location
 * @param type - The type of location (e.g., 'SITE')
 * @param parameters - The location attributes to update
 * @returns Promise resolving to the updated location data and request metadata
 * @throws Will throw an error if the location update fails or if the response
 *         doesn't match the expected schema
 */
export async function updateLocation(
  key: string,
  type: LocationType,
  parameters: UpdateLocationParameters
): Promise<UpdateLocationResponse> {
  const path = `/v1/location/${type}/${key}`;
  const { address, ...rest } = parameters;
  const body = {
    ...rest,
    ...(address !== undefined && {
      address: address
        ? {
            addressLine1: address.addressLine1,
            addressLine2: address.addressLine2,
            city: address.locality,
            state: address.administrativeArea,
            postalCode: address.postalCode,
            country: address.countryCode,
            formattedAddress: address.formattedAddress,
          }
        : address,
    }),
  };
  const response = await api.patch(path, body);

  return parseResponse(updateLocationResponseSchema, response.data, {
    schemaName: 'UpdateLocation',
    endpoint: path,
  });
}
